import classNames from 'classnames'
import { forwardRef } from 'react'
import { Link, LinkProps } from 'react-router'
import { Spinner } from '~/components/Button/Spinner'
import { focusRing } from '~/utils/styles'

type ButtonVariant = 'primary' | 'ghost' | 'text'
type PaddingOption = 'default' | 'none'

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  isLoading?: boolean
  padding?: PaddingOption
}

// The button styles should comply to joviva.de
// https://www.figma.com/design/DDCyqZra77viUa6nG6o6yG/Design-library?node-id=90-845&t=MMnpYBjVfUYh2zJZ-4
export const buttonClasses = {
  base: 'btn relative transition ease-in-out duration-150 flex items-center justify-center text-button1 rounded-2xl',
  padding: {
    default: 'px-[1.25rem] py-2',
  },
  variants: {
    primary:
      'bg-blue border-2 border-transparent text-teal-800 hover:shadow-button',
    ghost: 'text-default border-2 border-blue hover:shadow-button',
    text: 'hover:text-blue-500',
  },
  disabled: {
    primary:
      'disabled:bg-grey-200 disabled:text-grey-600 disabled:cursor-not-allowed disabled:shadow-none',
    ghost:
      'disabled:bg-grey-300 disabled:text-grey-500 disabled:border-2 disabled:border-grey-300 disabled:cursor-not-allowed disabled:shadow-none',
    text: 'disabled:text-grey-500 disabled:cursor-not-allowed disabled:shadow-none',
  },
  loading:
    'bg-grey-100 cursor-not-allowed text-neutral-800 disabled:shadow-none hover:bg-grey-200 hover:shadow-none',
}

export const buttonIconClasses = {
  left: 'pe-1 w-8 h-auto shrink-0',
  right: 'ps-1 w-8 h-auto shrink-0',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      isLoading,
      variant = 'primary',
      className,
      type = 'button',
      padding = 'default',
      ...props
    },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        className={classNames(
          // base styles
          focusRing,
          buttonClasses.base,
          buttonClasses.disabled[variant],
          padding !== 'none' && buttonClasses.padding[padding],
          // variant styles
          buttonClasses.variants[variant],
          // custom styles
          className,
          isLoading && buttonClasses.loading,
        )}
        type={type}
        aria-disabled={isLoading || !!props.disabled}
        aria-label={isLoading ? 'Bitte warten ...' : undefined}
        {...props}
      >
        {isLoading ? (
          <>
            <span className='absolute inset-0 flex items-center justify-center [&~*]:invisible'>
              <Spinner />
            </span>
            <span className='flex items-center opacity-0'>{children}</span>
          </>
        ) : (
          children
        )}
      </button>
    )
  },
)

Button.displayName = 'Button'

export interface ButtonLinkProps extends LinkProps {
  variant?: ButtonVariant
  padding?: PaddingOption
}

export const ButtonLink = forwardRef<HTMLAnchorElement, ButtonLinkProps>(
  (
    { children, variant = 'primary', padding = 'default', className, ...props },
    ref,
  ) => {
    return (
      <Link
        ref={ref}
        className={classNames(
          // base styles
          focusRing,
          buttonClasses.base,
          buttonClasses.disabled,
          padding !== 'none' && buttonClasses.padding[padding],
          // variant styles
          buttonClasses.variants[variant],
          // custom styles
          className,
        )}
        {...props}
      >
        {children}
      </Link>
    )
  },
)

ButtonLink.displayName = 'ButtonLink'
